import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar, Grid, IconButton, Stack, Box, TextField, Typography, useTheme, Skeleton } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

import { formatInZonedTime } from '../../../../utils/dateTime';
import useAuth from '../../../../hooks/useAuth';

const stringAvatar = (name) => ({
  sx: { width: 30, height: 30 },
  children: `${name?.charAt(0)?.toUpperCase() || ''}`,
});

const toDate = (dateString) => (dateString ? new Date(dateString) : null);

const ActionModalComment = ({ addComment, comments = [], loadingComments = false }) => {
  // -- Hooks --
  const theme = useTheme();
  const { t } = useTranslation();
  const { dateFormat, timezone } = useAuth();

  const [commentText, setCommentText] = useState('');
  // -- Hooks --

  // -- Function Handler(s) --
  const onCommentTextChangedHandler = (e) => {
    setCommentText(e.target.value);
  };

  const addCommentHandler = () => {
    if (addComment) {
      addComment(commentText);
    }

    setCommentText('');
  };
  // -- Function Handler(s) --

  // -- Render Contents --
  let commentContents = null;
  if (loadingComments) {
    commentContents = (
      <Stack direction="column" justifyContent="center" alignItems="stretch" spacing={1} sx={{ ml: 1 }}>
        <Skeleton animation="wave" width={619} height={40} />
        <Skeleton animation="wave" width={619} height={40} />
      </Stack>
    );
  } else if (!Array.isArray(comments) || !comments.length) {
    commentContents = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap',
          border: 1,
          borderRadius: 1,
          borderStyle: 'dashed',
          outline: 'none',
          width: '100%',
          ml: 1,
          py: 1
        }}
      >
        <Box
          sx={{
            '& img': {
              width: 100,
            },
          }}
        >
          <img alt="Select file" src="/static/images/undraw_no_data_kwbl.svg" />
        </Box>

        <Box sx={{ p: 2 }}>
          <Typography variant="h6">{t('Message_NoComments')}</Typography>
        </Box>
      </Box>
    );
  } else {
    commentContents = (
      <>
        {comments.map(({ NoteID, CreatedBy, CreatedOn, Content }) => (
          <Grid
            key={`ActionModelComment-${NoteID}`}
            item
            xs={12}
            sx={{
              p: 1,
              mt: 2,
              ml: 1,
              border: 1,
              borderRadius: 1,
              borderColor: theme.palette.background.default,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
                <Avatar {...stringAvatar(CreatedBy)} />

                <Typography variant="subtitle2">{CreatedBy}</Typography>
              </Stack>

              <Box sx={{ ml: 'auto', mr: 0.5 }}>
                <Typography variant="caption" display="block" color="primary">
                  {formatInZonedTime(toDate(CreatedOn), dateFormat, timezone)}
                </Typography>
              </Box>
            </Stack>

            <Typography variant="body2" gutterBottom sx={{ mx: 0.5, mt: 1 }}>
              {Content}
            </Typography>
          </Grid>
        ))}
      </>
    );
  }
  // -- Render Contents --

  return (
    <Grid item xs={12}>
      <Typography color="primary" variant="overline">
        {t('Label_Comments')}
      </Typography>

      <TextField
        fullWidth
        multiline
        rows={4}
        value={commentText}
        onChange={onCommentTextChangedHandler}
        name="ActionComment"
        placeholder={t('PlaceHolder_Comments')}
        size="small"
        InputProps={{
          endAdornment: (
            <IconButton
              color="primary"
              size="small"
              onClick={addCommentHandler}
              disabled={!commentText}
              sx={{
                mt: 'auto',
                backgroundColor: theme.palette.background.default,
              }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          ),
        }}
      />

      <Grid container spacing={1} sx={{ overflowY: 'auto', maxHeight: 220, mt: 1 }}>
        {commentContents}
      </Grid>
    </Grid>
  );
};

export default React.memo(ActionModalComment);
